import React, { useState, useEffect, useMemo } from 'react';
import { Search, MoreVertical, Eye, Edit, Trash2, X, Plus, Download, FileText, RefreshCw, CheckCircle, XCircle, Users } from 'lucide-react';
import Select from 'react-select';
import useFetch from '../constants/useFetch';
import { API_BASE_URL } from '../constants';
import * as XLSX from 'xlsx';
import ButtonSpinner from '../components/ButtonSpinner';

const ResultCodes = () => {
  const { data: resultData, loading, error, refetch } = useFetch(`${API_BASE_URL}/api/web/admin/result-codes`);
  const [resultCodes, setResultCodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [usedFilter, setUsedFilter] = useState('all');
  const [sortOption, setSortOption] = useState('created_at_desc');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [actionDropdownId, setActionDropdownId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(null);

  const [generateForm, setGenerateForm] = useState({
    count: 100,
    length: 10,
    max_usage: 10
  });

  useEffect(() => {
    if (resultData?.data && Array.isArray(resultData.data)) {
      setResultCodes(resultData.data);
    }
  }, [resultData?.data]);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  const usedOptions = [
    { value: 'all', label: 'All Usage' },
    { value: 'true', label: 'Used' },
    { value: 'false', label: 'Not Used' },
  ];

  const sortOptions = [
    { value: 'created_at_desc', label: 'Newest First' },
    { value: 'created_at_asc', label: 'Oldest First' },
    { value: 'code_asc', label: 'Code A-Z' },
    { value: 'code_desc', label: 'Code Z-A' },
  ];

  const filteredCodes = useMemo(() => {
    return resultCodes.filter((code) => {
      const matchesSearch = code.code?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || code.status === statusFilter;
      const isExpired = (code.current_usage || 0) >= (code.max_usage || 10);
      const matchesUsed = usedFilter === 'all' || 
        (usedFilter === 'true' && (code.is_used || isExpired)) ||
        (usedFilter === 'false' && !code.is_used && !isExpired);
      return matchesSearch && matchesStatus && matchesUsed;
    });
  }, [resultCodes, searchQuery, statusFilter, usedFilter]);

  const sortedCodes = useMemo(() => {
    let temp = [...filteredCodes];
    switch (sortOption) {
      case 'created_at_asc':
        temp.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'created_at_desc':
        temp.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'code_asc':
        temp.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
        break;
      case 'code_desc':
        temp.sort((a, b) => (b.code || '').localeCompare(a.code || ''));
        break;
      default:
        break;
    }
    return temp;
  }, [filteredCodes, sortOption]);

  const totalItems = filteredCodes.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedCodes = useMemo(() => {
    return sortedCodes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [sortedCodes, currentPage, itemsPerPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  useEffect(() => {
    const allSelected = paginatedCodes.length > 0 && paginatedCodes.every((code) => selectedCodes.includes(code.id));
    setSelectAll(allSelected);
  }, [selectedCodes, paginatedCodes]);

  const handleSelectAll = () => {
    if (selectedCodes.length === filteredCodes.length) {
      setSelectedCodes([]);
      setSelectAll(false);
    } else {
      setSelectedCodes(filteredCodes.map((c) => c.id));
      setSelectAll(true);
    }
  };

  const handleCodeSelect = (codeId) => {
    setSelectedCodes((prev) =>
      prev.includes(codeId) ? prev.filter((id) => id !== codeId) : [...prev, codeId]
    );
  };

  const handleActionClick = (id) => {
    setActionDropdownId(actionDropdownId === id ? null : id);
  };

  const closeDropdown = () => setActionDropdownId(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.action-dropdown')) setActionDropdownId(null);
    };
    if (actionDropdownId !== null) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [actionDropdownId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, usedFilter, itemsPerPage, sortOption]);

  const handleViewDetails = (code) => {
    setSelectedCode(code);
    setShowDetailsModal(true);
    closeDropdown();
  };

  const handleToggleStatus = async (codeId, resetUsage = false) => {
    try {
      setIsProcessing(codeId);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/web/admin/toggle-code-status/${codeId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ resetUsage }),
      });

      const data = await response.json();
      if (data.success) {
        alert(data.message);
        if (refetch) refetch();
      } else {
        alert(data.message || 'Failed to update code status');
      }
    } catch (err) {
      alert('Error updating code status');
    } finally {
      setIsProcessing(null);
      closeDropdown();
    }
  };

  const handleRegenerate = async (codeId) => {
    if (!window.confirm('Are you sure you want to regenerate this code? The old code will no longer work.')) {
      return;
    }

    try {
      setIsProcessing(codeId);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/web/admin/regenerate-code/${codeId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ length: 10 }),
      });

      const data = await response.json();
      if (data.success) {
        alert(`Code regenerated successfully! New code: ${data.code}`);
        if (refetch) refetch();
      } else {
        alert(data.message || 'Failed to regenerate code');
      }
    } catch (err) {
      alert('Error regenerating code');
    } finally {
      setIsProcessing(null);
      closeDropdown();
    }
  };

  const handleGenerateCodes = async (e) => {
    e.preventDefault();
    if (generateForm.count < 1 || generateForm.count > 1000) {
      alert('Count must be between 1 and 1000');
      return;
    }
    if (generateForm.length < 6 || generateForm.length > 20) {
      alert('Length must be between 6 and 20');
      return;
    }

    try {
      setIsGenerating(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/web/admin/generate-result-codes`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(generateForm),
      });

      const data = await response.json();
      if (data.success) {
        alert(`Successfully generated ${data.count} codes!`);
        setShowGenerateModal(false);
        setGenerateForm({ count: 100, length: 10, max_usage: 10 });
        if (refetch) refetch();
      } else {
        alert(data.message || 'Failed to generate codes');
      }
    } catch (err) {
      alert('Error generating codes');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/web/admin/export-codes-excel`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'result_codes.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Error exporting to Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/web/admin/export-codes-pdf`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'result_codes.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Error exporting to PDF');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const isExpired = (code) => {
    return (code.current_usage || 0) >= (code.max_usage || 10);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Result Codes</h1>
            <div className="flex flex-wrap items-center gap-4 text-blue-100">
              <span>{resultCodes.length} Codes</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExportExcel}
              className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export Excel</span>
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <FileText className="w-4 h-4" />
              <span>Export PDF</span>
            </button>
            <button
              onClick={() => setShowGenerateModal(true)}
              className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add New Codes</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Result Codes</h2>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search codes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              <Select
                value={statusOptions.find((option) => option.value === statusFilter)}
                onChange={(selected) => setStatusFilter(selected?.value || 'all')}
                options={statusOptions}
                className="w-48 text-gray-900 dark:text-white"
                placeholder="Filter by Status"
              />
              <Select
                value={usedOptions.find((option) => option.value === usedFilter)}
                onChange={(selected) => setUsedFilter(selected?.value || 'all')}
                options={usedOptions}
                className="w-48 text-gray-900 dark:text-white"
                placeholder="Filter by Usage"
              />
              <Select
                value={sortOptions.find((option) => option.value === sortOption)}
                onChange={(selected) => setSortOption(selected?.value || 'created_at_desc')}
                options={sortOptions}
                className="w-48 text-gray-900 dark:text-white"
                placeholder="Sort by"
              />
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {[10, 20, 30, 50].map((limit) => (
                  <option key={limit} value={limit}>{limit} per page</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4"><div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded"></div></td>
                    <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-300 dark:bg-gray-600 rounded"></div></td>
                    <td className="px-6 py-4"><div className="h-4 w-16 bg-gray-300 dark:bg-gray-600 rounded"></div></td>
                    <td className="px-6 py-4"><div className="h-4 w-16 bg-gray-300 dark:bg-gray-600 rounded"></div></td>
                    <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-300 dark:bg-gray-600 rounded"></div></td>
                    <td className="px-6 py-4"><div className="h-4 w-16 bg-gray-300 dark:bg-gray-600 rounded"></div></td>
                  </tr>
                ))
              ) : paginatedCodes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {error ? 'Error loading result codes' : 'No result codes found.'}
                  </td>
                </tr>
              ) : (
                paginatedCodes.map((code) => {
                  const expired = isExpired(code);
                  const usagePercent = ((code.current_usage || 0) / (code.max_usage || 10)) * 100;
                  return (
                    <tr key={code.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedCodes.includes(code.id)}
                          onChange={() => handleCodeSelect(code.id)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">{code.code}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          code.status === 'active' && !expired
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {code.status === 'active' && !expired ? 'Active' : expired ? 'Expired' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 min-w-[60px]">
                            <div
                              className={`h-2 rounded-full ${
                                expired ? 'bg-red-500' : usagePercent > 80 ? 'bg-yellow-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(usagePercent, 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {code.current_usage || 0}/{code.max_usage || 10}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(code.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap relative">
                        <button
                          className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 action-dropdown"
                          onClick={() => handleActionClick(code.id)}
                          disabled={isProcessing === code.id}
                        >
                          {isProcessing === code.id ? (
                            <ButtonSpinner size="small" />
                          ) : (
                            <MoreVertical className="w-5 h-5" />
                          )}
                        </button>
                        {actionDropdownId === code.id && (
                          <div className="absolute right-0 mt-2 z-50 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg action-dropdown">
                            <button
                              onClick={() => handleViewDetails(code)}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </button>
                            <button
                              onClick={() => handleToggleStatus(code.id, false)}
                              className="w-full flex items-center px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20"
                            >
                              {code.status === 'active' ? (
                                <>
                                  <XCircle className="w-4 h-4 mr-2" /> Invalidate
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2" /> Validate
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleToggleStatus(code.id, true)}
                              className="w-full flex items-center px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/20"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" /> Reset Usage
                            </button>
                            <button
                              onClick={() => handleRegenerate(code.id)}
                              className="w-full flex items-center px-4 py-2 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/20"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" /> Regenerate Code
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {(currentPage - 1) * itemsPerPage + 1}-
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} codes
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  currentPage === page ? 'bg-blue-600 text-white dark:bg-blue-700' : ''
                }`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Generate Codes Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Generate Result Codes</h2>
              <button
                onClick={() => setShowGenerateModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleGenerateCodes} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Number of Codes
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={generateForm.count}
                  onChange={(e) => setGenerateForm({ ...generateForm, count: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Between 1 and 1000</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Code Length (characters)
                </label>
                <input
                  type="number"
                  min="6"
                  max="20"
                  value={generateForm.length}
                  onChange={(e) => setGenerateForm({ ...generateForm, length: parseInt(e.target.value) || 10 })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Between 6 and 20 (default: 10)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Usage per Code
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={generateForm.max_usage}
                  onChange={(e) => setGenerateForm({ ...generateForm, max_usage: parseInt(e.target.value) || 10 })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Default: 10</p>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isGenerating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <ButtonSpinner size="small" color="white" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Generate Codes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Result Code Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Code</label>
                <p className="text-gray-900 dark:text-white font-mono">{selectedCode.code}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</label>
                <p className="text-gray-900 dark:text-white">{selectedCode.status}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Usage</label>
                <p className="text-gray-900 dark:text-white">
                  {selectedCode.current_usage || 0} / {selectedCode.max_usage || 10}
                  {isExpired(selectedCode) && <span className="text-red-600 ml-2">(Expired)</span>}
                </p>
              </div>
              {selectedCode.used_by && Array.isArray(selectedCode.used_by) && selectedCode.used_by.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Used By ({selectedCode.used_by.length} students)
                  </label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {selectedCode.used_by.map((usage, idx) => (
                      <div key={idx} className="text-sm bg-gray-50 dark:bg-gray-700 p-2 rounded">
                        <p className="text-gray-900 dark:text-white font-medium">{usage.student_name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {usage.used_at ? new Date(usage.used_at).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Created At</label>
                <p className="text-gray-900 dark:text-white">{formatDate(selectedCode.created_at)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Updated At</label>
                <p className="text-gray-900 dark:text-white">{formatDate(selectedCode.updated_at)}</p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultCodes;
